<template>
  <div id="login" class="fill-height align-start">
    <v-row
      class="g-container"
      v-if="!loading && !handlingAuthCallback"
      align="center"
      justify="center"
      no-gutters
    >
      <v-col v-if="$vuetifyDisplay.smAndDown">
        <v-card
          v-if="downForMaintenance"
          class="rounded-lg pa-4 pb-4 mb-4"
          outlined
        >
          <v-card-title class="pa-0 pb-1">
            <h1 class="g-text-color-primary g-heading-l">
              <v-icon large color="primary">mdi-connection</v-icon> We'll be
              back soon!
            </h1>
          </v-card-title>
          <v-card-text class="px-0 py-4 text-body-1 align-start">
            <p>
              We're very sorry for the inconvenience but we're currently
              performing some important system maintenance. Please check back
              soon...
            </p>
            <p class="pt-8 font-italic">-- FLEWiD Team</p>
          </v-card-text>
        </v-card>
        <v-card v-if="blockedByWaf" class="rounded-lg pa-4 pb-4 mb-4" outlined>
          <v-card-title class="pa-0 pb-1">
            <h1 class="g-text-color-primary g-heading-l">
              <v-icon large color="primary">mdi-shield-account</v-icon> Too many
              requests!
            </h1>
          </v-card-title>
          <v-card-text class="px-0 py-4 text-body-1 align-start">
            <p>
              We're sorry, but you've been rate limited for security purposes.
            </p>
            <p>Please wait a few minutes before trying again.</p>
            <br />
            <p>
              If you continue to receive this error, please contact us on
              <a
                class="i-a-link"
                href="mailto:support@flewidtech.com"
                target="_blank"
                >support@flewidtech.com</a
              >
              for assistance.
            </p>
          </v-card-text>
        </v-card>
        <v-card v-else class="rounded-lg pa-4 pb-6 mb-4" outlined>
          <v-card-title class="pa-0 pb-1">
            <h1 class="g-text-color-primary g-heading-l">
              {{ isILR ? 'Supplier Marketplace' : 'Supplier Portal' }}
            </h1>
          </v-card-title>

          <v-card-text class="px-0 py-4 align-start">
            <div class="mb-2">
              <v-chip small class="p-chip mr-2 mb-1 grey lighten-2"
                ><strong>Range</strong></v-chip
              >
              <v-chip small class="p-chip mr-2 mb-1 grey lighten-2"
                ><strong>Offers</strong></v-chip
              >
              <v-chip small class="p-chip mr-2 mb-1 grey lighten-2"
                ><strong>Orders</strong></v-chip
              >
              <v-chip small class="p-chip mr-2 mb-1 grey lighten-2"
                ><strong>Payout</strong></v-chip
              >
            </div>
            <ul class="p-benefits-list mb-6">
              <li>Showcase your Range</li>
              <li>Curate trading arrangements & offers</li>
              <li>
                Receive & manage {{ isILR ? 'Customers' : 'Retailer' }} orders
                via the Portal
              </li>
              <li>Simple efficient Payout process</li>
            </ul>

            <v-btn
              rounded
              large
              type="submit"
              class="i-btn-block-secondary p-btn"
              :loading="redirecting"
              @click.prevent="goToLogin"
            >
              Login<v-icon class="pl-1 pt-1" small>mdi-chevron-right</v-icon>
            </v-btn>
            <v-btn
              v-if="signupFlow !== 'NONE' && !isILR"
              rounded
              large
              class="i-btn-block-primary-outlined p-btn mt-2 mt-sm-0 ml-sm-2"
              :to="{ name: 'register' }"
            >
              Sign up<v-icon class="pl-1 pt-1" small>mdi-chevron-right</v-icon>
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col v-else>
        <v-card
          v-if="downForMaintenance"
          class="d-flex rounded-lg pa-4 pb-6 px-md-8 px-lg-15 pt-md-10 pb-md-11"
          outlined
        >
          <v-col cols="6" offset="3" md="6" class="px-4 py-0">
            <v-card-title class="pa-0 pb-2">
              <h1 class="g-text-color-primary g-heading-l">
                <v-icon large color="primary">mdi-connection</v-icon> We'll be
                back soon!
              </h1>
            </v-card-title>
            <v-card-text class="px-0 py-8 text-body-1 align-start">
              <p>
                We're very sorry for the inconvenience but we're currently
                performing some important system maintenance. Please check back
                soon...
              </p>
              <p class="pt-10 font-italic">-- FLEWiD Team</p>
            </v-card-text>
          </v-col>
        </v-card>
        <v-card v-if="blockedByWaf" class="rounded-lg pa-4 pb-4 mb-4" outlined>
          <v-card-title class="pa-0 pb-1">
            <h1 class="g-text-color-primary g-heading-l">
              <v-icon large color="primary">mdi-shield-account</v-icon> Too many
              requests!
            </h1>
          </v-card-title>
          <v-card-text class="px-0 py-4 text-body-1 align-start">
            <p>
              We're sorry, but you've been rate limited for security purposes.
            </p>
            <p>Please wait a few minutes before trying again.</p>
            <br />
            <p>
              If you continue to receive this error, please contact us on
              <a
                class="i-a-link"
                href="mailto:support@flewidtech.com"
                target="_blank"
                >support@flewidtech.com</a
              >
              for assistance.
            </p>
          </v-card-text>
        </v-card>
        <v-card
          v-else
          class="p-login-card-desktop d-flex rounded-lg pa-4 pb-6 px-md-8 px-lg-15 pt-md-10 pb-md-11"
          outlined
        >
          <v-col cols="12" md="6" class="p-logo-div pa-0 pr-md-8 pr-lg-16">
            <img
              class="p-logo-image"
              :alt="`${tenantName} logo`"
              :src="tenantLogoDark"
            />
          </v-col>

          <v-divider class="mx-0" vertical></v-divider>

          <v-col
            cols="12"
            md="6"
            class="p-content-div pa-0 pl-md-8 pl-lg-16 my-auto"
          >
            <v-card-title class="pa-0 pb-1">
              <h1 class="g-text-color-primary g-heading-l">
                {{ isILR ? 'Supplier Marketplace' : 'Supplier Portal' }}
              </h1>
            </v-card-title>
            <v-card-text class="px-0 py-4 align-start">
              <div class="mb-2">
                <v-chip small class="p-chip mr-2 mb-1 grey lighten-2"
                  ><strong>Range</strong></v-chip
                >
                <v-chip small class="p-chip mr-2 mb-1 grey lighten-2"
                  ><strong>Offers</strong></v-chip
                >
                <v-chip small class="p-chip mr-2 mb-1 grey lighten-2"
                  ><strong>Orders</strong></v-chip
                >
                <v-chip small class="p-chip mr-2 mb-1 grey lighten-2"
                  ><strong>Payout</strong></v-chip
                >
              </div>
              <ul class="p-benefits-list mb-6">
                <li>Showcase your Range</li>
                <li>Curate trading arrangements & offers</li>
                <li>
                  Receive & manage {{ isILR ? 'Customers' : 'Retailer' }} orders
                  via the Portal
                </li>
                <li>Simple efficient Payout process</li>
              </ul>

              <v-row>
                <v-btn
                  rounded
                  large
                  class="i-btn-block-secondary p-btn"
                  type="submit"
                  :loading="redirecting"
                  @click.prevent="goToLogin"
                >
                  Login<v-icon class="pl-1 pt-1" small
                    >mdi-chevron-right</v-icon
                  >
                </v-btn>
                <template v-if="signupFlow !== 'NONE' && !isILR">
                  <span class="py-3 px-4 font-weight-bold"> OR </span>
                  <v-btn
                    rounded
                    large
                    class="i-btn-block-primary-outlined p-btn"
                    :to="{ name: 'register' }"
                  >
                    Sign up<v-icon class="pl-1 pt-1" small
                      >mdi-chevron-right</v-icon
                    >
                  </v-btn>
                </template>
              </v-row>
            </v-card-text>
          </v-col>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import store from '@/store'

export default {
  data: () => ({
    redirecting: false,
    handlingAuthCallback: false
  }),

  computed: {
    ...mapState('tenant', ['loading', 'downForMaintenance', 'blockedByWaf']),
    ...mapGetters('preferences', ['supplierSelected']),
    ...mapGetters('tenant', [
      'tenantLogoDark',
      'tenantLogoLight',
      'tenantName',
      'isILR',
      'signupFlow'
    ])
  },

  methods: {
    ...mapActions('auth', ['login', 'setToken']),
    ...mapActions('notifications', ['addNotification']),

    goToSignUp() {
      this.$router.push({ name: 'register' })
    },

    // Calls to Auth0 for login which redirects to this same page but including a hash in the URL
    goToLogin() {
      this.redirecting = true
      this.login().catch((err) => {
        if (err.description) {
          this.addNotification({ color: 'error', text: err.description })
        } else {
          this.addNotification({ color: 'error', defaultError: true })
        }
      })
    }
  },

  // When page loads with a hash in URL (after login call) it then sets token and fetches user data from Auth0
  mounted() {
    const queryParams = this.$route.query || {}
    if (queryParams.code && queryParams.state) {
      this.handlingAuthCallback = true
      store
        .dispatch('auth/setToken')
        .then(() => {
          // not redirecting to previous selected page due to auth0 not working with redirection URL containing query params
          if (this.supplierSelected) {
            this.$router.push('/')
          } else {
            this.$router.push({ name: 'pick-supplier' })
          }
        })
        .catch((err) => {
          console.log('error on setting auth0 token', err)
          this.handlingAuthCallback = false
          this.addNotification({
            color: 'error',
            defaultError: true,
            timeout: -1
          })
        })
    } else if (queryParams.error && queryParams.error_description) {
      this.addNotification({
        color: 'error',
        text: queryParams.error_description,
        timeout: -1
      })
    } else if (queryParams.message && queryParams.success) {
      const isSuccess = queryParams.success === 'true'
      const isEmailVerifiedSuccessfullyMessage =
        queryParams.message.indexOf('verified') !== -1 && isSuccess
      const portalName = this.isILR ? 'Supplier Marketplace' : 'Supplier Portal'
      const text = isEmailVerifiedSuccessfullyMessage
        ? `Your email was successfully verified and you can now Login to the ${portalName}.`
        : queryParams.message
      this.addNotification({
        color: isSuccess ? '#06A8F3' : 'error',
        text,
        timeout: -1
      })
    }
  }
}
</script>

<style lang="scss" scoped>
#login {
  padding-top: 4vh;
  hr {
    min-height: 100px;
    border-color: rgba(0, 0, 0, 0.12);
    min-width: 5px;
  }
  .p-benefits-list li {
    line-height: 1.8em !important;
  }
  .p-btn {
    width: 100%;
  }
  .p-chip {
    background-color: $blue-color-bg;
    text-transform: uppercase;
    * {
      font-size: 12px !important;
      letter-spacing: 0.5px;
    }
  }
  .p-logo-div {
    display: flex;
    align-items: center;
    .p-logo-image {
      width: 100%;
    }
  }
}

@media only screen and (min-width: 600px) {
  #login {
    .p-btn {
      width: 160px;
    }
  }
}

@media #{map-get($display-breakpoints, 'md-and-up')} {
  #login {
    .p-login-card-desktop {
      display: flex !important;
      flex-direction: row !important;
    }

    .p-logo-div,
    .p-content-div {
      flex: 0 0 50%;
      max-width: 50%;
    }

    .p-benefits-list li {
      line-height: 2em !important;
    }
    .p-chip {
      * {
        font-size: 14px !important;
        letter-spacing: 0.5px;
      }
    }
  }
}
</style>
